import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

const LANGUAGE_LOCALE = {
  en: "en-IN",
  hi: "hi-IN",
  kn: "kn-IN",
  te: "te-IN",
  mr: "mr-IN",
};

export default function ParentMessages() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState("");

  const locale = LANGUAGE_LOCALE[i18n.language] || "en-IN";

  useEffect(() => {
    api.get("/parent/messages").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  const open = async (m) => {
    const opening = openId !== m.id;
    setOpenId(opening ? m.id : "");

    if (opening && !m.is_read) {
      try {
        await api.post(`/parent/messages/${m.id}/read`);
        setRows((prev) =>
          prev.map((r) =>
            r.id === m.id ? { ...r, is_read: true } : r
          )
        );
      } catch {}
    }
  };

  const formatMessageDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div data-testid="parent-messages" className="px-5 py-5 space-y-3">
      <h2 className="font-heading font-extrabold text-lg">
        {t("parentMessages.heading")}
      </h2>

      <p className="text-xs text-slate-400 -mt-1">
        {t("parentMessages.description")}
      </p>

      {rows.map((m) => (
        <button
          key={m.id}
          data-testid={`pm-row-${m.id}`}
          onClick={() => open(m)}
          className={`w-full text-left bg-white border rounded-2xl p-4 transition-colors duration-200 ${
            m.is_read
              ? "border-slate-200"
              : "border-brand-coral/40 bg-brand-coral/[0.03]"
          }`}
        >
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {!m.is_read && (
              <span
                data-testid={`pm-unread-${m.id}`}
                className="w-2 h-2 rounded-full bg-brand-coral shrink-0"
              />
            )}

            <span
              className={`font-bold text-sm ${
                m.is_read ? "text-slate-700" : "text-slate-900"
              }`}
            >
              {m.teacher_name}
            </span>

            <span className="bg-[#F4F5F7] rounded-full px-2.5 py-0.5 font-semibold">
              {m.purpose}
            </span>

            <span className="ml-auto">
              {formatMessageDate(m.created_at)}
            </span>
          </div>

          <p className="text-xs text-slate-400 mt-1">
            {t("parentMessages.for")} {m.student_name} · {m.class_name}
            {m.subject_name ? ` · ${m.subject_name}` : ""}
          </p>

          <p
            className={`text-sm mt-1.5 whitespace-pre-wrap ${
              openId === m.id
                ? "text-slate-700"
                : "text-slate-500 line-clamp-2"
            }`}
          >
            {m.message}
          </p>

          {openId === m.id && m.is_read && (
            <p className="text-[10px] text-slate-300 mt-2">
              {t("parentMessages.readOneWay")}
            </p>
          )}
        </button>
      ))}

      {rows.length === 0 && (
        <p
          className="text-sm text-slate-400 py-8 text-center"
          data-testid="pm-empty"
        >
          {t("parentMessages.noMessages")}
        </p>
      )}
    </div>
  );
}